import truncate from 'lodash/truncate'

import { CANONICAL_ORIGIN, absoluteUrl, aggregateRating } from './shared'

// The redux `listing.data` shape (snake_case, from listing_serializer.rb).
export interface DetailListing {
  id?: number | string
  title?: string
  description?: string
  price?: number | string
  currency?: string
  review_score?: number | string
  review_count?: number
  promo_image?: { medium?: string }
  bedrooms?: number
  city?: string
  state?: string
  country?: string
  postcode?: string
  location?: { lat?: number; lng?: number }
  listing_type?: { slug?: string }
  url?: string
}

// The listing detail page as schema.org Product (House for accommodation).
export const listingProductJsonLd = (
  listing: DetailListing | null | undefined,
  { origin = CANONICAL_ORIGIN }: { origin?: string } = {},
) => {
  if (!listing) return null

  const accommodation = listing.listing_type?.slug === 'accommodation'
  const url = absoluteUrl(listing.url, origin)

  const addressFields = [
    listing.city,
    listing.state,
    listing.country,
    listing.postcode,
  ]
  const address = addressFields.some(Boolean)
    ? {
        '@type': 'PostalAddress',
        addressLocality: listing.city,
        addressRegion: listing.state,
        addressCountry: listing.country,
        postalCode: listing.postcode,
      }
    : undefined

  return {
    '@context': 'https://schema.org',
    '@type': accommodation ? ['House', 'Product'] : ['Product'],
    '@id': url,
    name: listing.title,
    brand: 'Riparide',
    description: listing.description
      ? truncate(listing.description, { length: 232 })
      : undefined,
    identifier: listing.id,
    productID: listing.id,
    url,
    image: listing.promo_image?.medium,
    offers: {
      '@type': 'Offer',
      businessFunction: 'http://purl.org/goodrelations/v1#LeaseOut',
      price: listing.price == null ? undefined : Number(listing.price),
      priceCurrency: listing.currency?.toUpperCase(),
      availability: 'https://schema.org/InStock',
    },
    numberOfRooms: listing.bedrooms,
    address,
    aggregateRating: aggregateRating({
      reviewScore: listing.review_score,
      reviewCount: listing.review_count,
    }),
    geo:
      accommodation && listing.location
        ? {
            '@type': 'GeoCoordinates',
            latitude: listing.location.lat,
            longitude: listing.location.lng,
          }
        : undefined,
  }
}
